import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiArrowsClockwiseBold, PiCheckCircleBold, PiDownloadSimpleBold, PiFilePdfBold, PiHashBold, PiXBold } from 'react-icons/pi';
import { Button, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import {
    canExportPloomesDeal as canExportPloomesDealPermission,
    canSyncPloomesDeal,
    isSuperAdmin,
} from '@v2/lib/auth';
import ClientAvatars from '@v2/components/ui/ClientAvatars';
import V2Modal from '@v2/components/ui/V2Modal';
import CommissionPeriodReportModal from '@v2/features/ploomes-deals/CommissionPeriodReportModal';
import PloomesDealBulkBar from '@v2/features/ploomes-deals/PloomesDealBulkBar';
import PloomesDealImportConfirmModal from '@v2/features/ploomes-deals/PloomesDealImportConfirmModal';
import PloomesDealImportPreviewPanel from '@v2/features/ploomes-deals/PloomesDealImportPreviewPanel';
import PloomesDealListFilters from '@v2/features/ploomes-deals/PloomesDealListFilters';
import PloomesDealListTable from '@v2/features/ploomes-deals/PloomesDealListTable';
import type { FilterOptions, PloomesDealFilters, PloomesDealRow } from '@v2/features/ploomes-deals/types';
import { useI18n } from '@v2/providers/I18nProvider';

function makeDefaultFilters(): PloomesDealFilters {
    return {
        search: '',
        pipeline_name: '',
        stage_name: '',
        status_name: '',
        defaulted_only: '',
        open_installments_only: '',
        owner_names: [],
        account_executive: '',
        segment: '',
        client_classifications: [],
        finish_date_from: '',
        finish_date_to: '',
        updated_from: '',
        updated_to: '',
        amount_min: '',
        amount_max: '',
        buckler_one: '',
        team_agreement: '',
        commission_exception: '',
        has_sdr: '',
        page: 1,
        per_page: 25,
        sort: '-finish_date',
    };
}

function buildParams(filters: PloomesDealFilters): Record<string, string | number | string[]> {
    const params: Record<string, string | number | string[]> = {
        page: filters.page,
        per_page: filters.per_page,
        sort: filters.sort,
    };
    (Object.keys(filters) as Array<keyof PloomesDealFilters>).forEach((key) => {
        if (['page', 'per_page', 'sort'].includes(key)) return;
        const v = filters[key];
        if (Array.isArray(v)) {
            if (v.length > 0) params[key] = v;
            return;
        }
        if (v !== '' && v !== undefined) {
            params[key] = v;
        }
    });
    return params;
}

type CreatedShipment = {
    id: number;
    order_number: string | null;
    supplier: string | null;
    deals_count: number;
    linked_deals: Array<{ id: number; label: string }>;
    clients_summary: string[];
    url: string;
};

type BulkResponse = {
    status: 'created' | 'skipped' | 'empty_supplier';
    message: string;
    created: CreatedShipment[];
    skipped: Array<{ supplier: string; deal_ids: number[] }>;
};

export default function PloomesDealListPage() {
    const qc = useQueryClient();
    const { t } = useI18n();
    const { data: me } = useQuery({
        queryKey: ['me'],
        queryFn: async () => {
            const { data } = await api.get<{ permissions?: string[] }>('/me');
            return data;
        },
        staleTime: 60_000,
    });
    const canExportPloomesDeal =
        canExportPloomesDealPermission() ||
        Boolean(me?.permissions?.includes('Export:PloomesDeal'));
    const canCommissionReport = isSuperAdmin();
    const [filters, setFilters] = useState<PloomesDealFilters>(() => makeDefaultFilters());
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [syncConfirmOpen, setSyncConfirmOpen] = useState(false);
    const [importConfirmOpen, setImportConfirmOpen] = useState(false);
    const [commissionReportOpen, setCommissionReportOpen] = useState(false);
    const [resultOpen, setResultOpen] = useState(false);
    const [lastCreated, setLastCreated] = useState<CreatedShipment[]>([]);

    const [showBulkSelect, setShowBulkSelect] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);

    const handleExport = useCallback(async () => {
        setExportLoading(true);
        try {
            const params = new URLSearchParams();
            (Object.keys(filters) as Array<keyof PloomesDealFilters>).forEach((key) => {
                if (['page', 'per_page'].includes(key)) return;
                const v = filters[key];
                if (Array.isArray(v)) {
                    v.forEach((item) => params.append(`${key}[]`, item));
                    return;
                }
                if (v !== '' && v !== undefined) params.append(key, String(v));
            });

            const response = await api.get('/ploomes-deals/export', {
                params: Object.fromEntries(params),
                responseType: 'blob',
            });

            const url  = URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const link = document.createElement('a');
            link.href  = url;
            link.setAttribute('download', `relatorio-comissoes-${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch {
            toast.error('Erro ao gerar relatório. Tente novamente.');
        } finally {
            setExportLoading(false);
        }
    }, [filters]);
    const [bulkTags, setBulkTags] = useState<string[]>([]);
    const [bulkInput, setBulkInput] = useState('');
    const [bulkSelectResult, setBulkSelectResult] = useState<{ added: number; notFound: string[] } | null>(null);
    const bulkInputRef = useRef<HTMLInputElement>(null);

    const addBulkTags = useCallback((text: string) => {
        const parts = text.split(/[,;\s\n]+/).map((s) => s.trim()).filter((s) => /^\d+$/.test(s));
        if (parts.length > 0) {
            setBulkTags((prev) => [...new Set([...prev, ...parts])]);
        }
    }, []);

    const removeBulkTag = useCallback((tag: string) => {
        setBulkTags((prev) => prev.filter((t) => t !== tag));
        setBulkSelectResult(null);
    }, []);

    const handleBulkInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setBulkSelectResult(null);
        if (/[,;\s\n]/.test(val)) {
            addBulkTags(val);
            setBulkInput('');
        } else {
            setBulkInput(val);
        }
    }, [addBulkTags]);

    const handleBulkInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
            e.preventDefault();
            const val = bulkInput.trim();
            if (/^\d+$/.test(val)) {
                setBulkTags((prev) => [...new Set([...prev, val])]);
                setBulkInput('');
            }
        } else if (e.key === 'Backspace' && bulkInput === '' && bulkTags.length > 0) {
            setBulkTags((prev) => prev.slice(0, -1));
            setBulkSelectResult(null);
        }
    }, [bulkInput, bulkTags.length]);

    const handleBulkInputBlur = useCallback(() => {
        const val = bulkInput.trim();
        if (/^\d+$/.test(val)) {
            setBulkTags((prev) => [...new Set([...prev, val])]);
            setBulkInput('');
        }
    }, [bulkInput]);

    const resolveNumbersMutation = useMutation({
        mutationFn: async (numbers: string[]) => {
            const { data } = await api.post<{
                matches: { id: number; deal_number: string }[];
                not_found: string[];
            }>('/ploomes-deals/resolve-by-numbers', { numbers });
            return data;
        },
        onSuccess: (data) => {
            const newIds = data.matches.map((m) => m.id);
            setSelectedIds((prev) => {
                const next = new Set(prev);
                newIds.forEach((id) => next.add(id));
                return next;
            });
            setBulkSelectResult({ added: newIds.length, notFound: data.not_found });
            setBulkTags([]);
            setBulkInput('');
        },
        onError: () => toast.error('Erro ao resolver números. Tente novamente.'),
    });

    const handleBulkSelectSubmit = useCallback(() => {
        const numbers = [...bulkTags, ...(bulkInput.trim() ? [bulkInput.trim()] : [])].filter((s) => /^\d+$/.test(s));
        if (numbers.length === 0) return;
        setBulkSelectResult(null);
        resolveNumbersMutation.mutate(numbers);
    }, [bulkTags, bulkInput, resolveNumbersMutation]);

    const selectedIdsArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

    const queryParams = useMemo(() => buildParams(filters), [filters]);

    const { data: filterOptions } = useQuery({
        queryKey: ['ploomes-deals', 'filter-options'],
        queryFn: async () => {
            const { data } = await api.get<FilterOptions>('/ploomes-deals/filter-options');
            return data;
        },
    });

    const { data: listResponse, isLoading, isFetching } = useQuery({
        queryKey: ['ploomes-deals', 'list', queryParams],
        queryFn: async () => {
            const { data } = await api.get<{ data: PloomesDealRow[]; meta: { current_page: number; last_page: number; total: number } }>(
                '/ploomes-deals',
                { params: queryParams },
            );
            return data;
        },
        placeholderData: (prev) => prev,
    });

    const bulkMutation = useMutation({
        mutationFn: async (ids: number[]) => {
            const { data } = await api.post<BulkResponse>('/ploomes-deals/bulk-create-import-request', { ids });
            return data;
        },
        onSuccess: (data) => {
            setImportConfirmOpen(false);
            if (data.status === 'created') {
                toast.success(data.message);
                setLastCreated(data.created);
                setResultOpen(true);
            } else {
                toast.error(data.message);
            }
            setSelectedIds(new Set());
            qc.invalidateQueries({ queryKey: ['ploomes-deals', 'list'] });
            qc.invalidateQueries({ queryKey: ['shipments'] });
        },
        onError: () => toast.error('Falha ao criar requisição de importação'),
    });

    const syncAllMutation = useMutation({
        mutationFn: async () => {
            const { data } = await api.post<{ message: string }>('/ploomes-deals/sync-all', {
                finish_date_from: filters.finish_date_from || undefined,
                finish_date_to: filters.finish_date_to || undefined,
            });
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message);
            qc.invalidateQueries({ queryKey: ['ploomes-deals'] });
        },
        onError: () => toast.error('Falha ao enfileirar sincronização.'),
    });

    const patchFilters = useCallback((patch: Partial<PloomesDealFilters>) => {
        setFilters((prev) => ({ ...prev, ...patch }));
    }, []);

    const clearFilters = useCallback(() => {
        setFilters(makeDefaultFilters());
    }, []);

    const toggleRow = useCallback((id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleAll = useCallback((ids: number[]) => {
        setSelectedIds((prev) => {
            const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
            return allSelected ? new Set() : new Set(ids);
        });
    }, []);

    const handleBulkCreate = useCallback(() => {
        if (selectedIdsArray.length === 0) return;
        setImportConfirmOpen(true);
    }, [selectedIdsArray.length]);

    const confirmBulkCreate = useCallback(() => {
        bulkMutation.mutate(selectedIdsArray);
    }, [selectedIdsArray, bulkMutation]);

    const rows = listResponse?.data ?? [];
    const meta = listResponse?.meta;

    return (
        <div className="flex flex-col gap-6">
            <PageHeader
                title="Propostas Ploomes"
                breadcrumb={[{ name: 'Dashboard', href: '/' }, { name: 'Propostas Ploomes' }]}
            >
                <Button
                    variant="outline"
                    className="w-full @lg:w-auto"
                    onClick={() => { setShowBulkSelect((v) => !v); setBulkSelectResult(null); setBulkTags([]); setBulkInput(''); }}
                >
                    <PiHashBold className="me-1.5 h-[17px] w-[17px]" />
                    Selecionar por número
                </Button>
                {canExportPloomesDeal && (
                    <Button
                        variant="outline"
                        className="w-full @lg:w-auto"
                        onClick={handleExport}
                        isLoading={exportLoading}
                    >
                        <PiDownloadSimpleBold className="me-1.5 h-[17px] w-[17px]" />
                        Extrair Excel
                    </Button>
                )}
                {canCommissionReport && (
                    <Button
                        variant="outline"
                        className="w-full @lg:w-auto"
                        onClick={() => setCommissionReportOpen(true)}
                    >
                        <PiFilePdfBold className="me-1.5 h-[17px] w-[17px]" />
                        {t('v2.ploomes_deals.commission_report.button')}
                    </Button>
                )}
                {canSyncPloomesDeal() && (
                    <Button
                        className="w-full @lg:w-auto"
                        onClick={() => setSyncConfirmOpen(true)}
                        isLoading={syncAllMutation.isPending}
                    >
                        <PiArrowsClockwiseBold className="me-1.5 h-[17px] w-[17px]" />
                        Sincronizar Ploomes
                    </Button>
                )}
            </PageHeader>

            {showBulkSelect && (
                <div className="ds-card p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            Selecionar propostas por número
                        </p>
                        <button
                            type="button"
                            onClick={() => { setShowBulkSelect(false); setBulkSelectResult(null); setBulkTags([]); setBulkInput(''); }}
                            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                        >
                            <PiXBold className="size-4" />
                        </button>
                    </div>
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                        Digite ou cole números separados por vírgula, espaço ou Enter. Cada número vira um badge.
                    </p>

                    {/* Tag input */}
                    <div
                        className="flex min-h-[44px] cursor-text flex-wrap gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1.5 transition-colors focus-within:border-primary focus-within:ring-1 focus-within:ring-primary dark:border-gray-700 dark:bg-gray-900"
                        onClick={() => bulkInputRef.current?.focus()}
                    >
                        {bulkTags.map((tag) => (
                            <span
                                key={tag}
                                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary-dark dark:text-primary"
                            >
                                {tag}
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); removeBulkTag(tag); }}
                                    className="rounded-full p-0.5 hover:bg-primary/20"
                                    aria-label={`Remover ${tag}`}
                                >
                                    <PiXBold className="size-2.5" />
                                </button>
                            </span>
                        ))}
                        <input
                            ref={bulkInputRef}
                            type="text"
                            inputMode="numeric"
                            value={bulkInput}
                            onChange={handleBulkInputChange}
                            onKeyDown={handleBulkInputKeyDown}
                            onBlur={handleBulkInputBlur}
                            placeholder={bulkTags.length === 0 ? '971, 974, 975…' : ''}
                            className="min-w-[100px] flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400 dark:placeholder:text-gray-600"
                        />
                    </div>

                    {bulkSelectResult && (
                        <div className={`mt-2 rounded-lg px-3 py-2 text-sm ${
                            bulkSelectResult.notFound.length > 0
                                ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300'
                                : 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300'
                        }`}>
                            <span className="inline-flex items-center gap-1 font-medium">
                                <PiCheckCircleBold className="size-4" />
                                {bulkSelectResult.added} proposta(s) selecionada(s).
                            </span>
                            {bulkSelectResult.notFound.length > 0 && (
                                <p className="mt-1 text-xs">
                                    Não encontrados: <strong>{bulkSelectResult.notFound.join(', ')}</strong>
                                </p>
                            )}
                        </div>
                    )}

                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                            {bulkTags.length > 0 ? `${bulkTags.length} número(s)` : ''}
                        </span>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setBulkTags([]); setBulkInput(''); setBulkSelectResult(null); }}
                                disabled={bulkTags.length === 0 && !bulkInput}
                            >
                                Limpar
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleBulkSelectSubmit}
                                disabled={(bulkTags.length === 0 && !bulkInput.trim()) || resolveNumbersMutation.isPending}
                                isLoading={resolveNumbersMutation.isPending}
                            >
                                Selecionar
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <V2Modal
                open={syncConfirmOpen}
                onClose={() => setSyncConfirmOpen(false)}
                title="Sincronizar Ploomes"
                description={
                    filters.finish_date_from && filters.finish_date_to
                        ? `Vamos buscar no Ploomes as propostas ganhas de ${filters.finish_date_from} a ${filters.finish_date_to} (mesmo período do filtro da lista).`
                        : 'Vamos buscar no Ploomes as propostas ganhas dos últimos 30 dias (evita lacuna na virada do mês).'
                }
                size="md"
                footer={
                    <>
                        <Button variant="outline" onClick={() => setSyncConfirmOpen(false)} disabled={syncAllMutation.isPending}>
                            Cancelar
                        </Button>
                        <Button
                            color="warning"
                            onClick={() => {
                                setSyncConfirmOpen(false);
                                syncAllMutation.mutate();
                            }}
                            isLoading={syncAllMutation.isPending}
                        >
                            <PiArrowsClockwiseBold className="me-2 size-4" />
                            Sincronizar agora
                        </Button>
                    </>
                }
            >
                <p className="text-sm text-gray-600">
                    Não puxa só os 10 primeiros: sincroniza o período filtrado (por padrão, os últimos 30 dias).
                    Os dados aparecem aos poucos em segundo plano — recarregue em alguns minutos.
                </p>
            </V2Modal>

            <CommissionPeriodReportModal
                open={commissionReportOpen}
                onClose={() => setCommissionReportOpen(false)}
            />

            <PloomesDealImportConfirmModal
                open={importConfirmOpen}
                selectedIds={selectedIdsArray}
                loading={bulkMutation.isPending}
                onClose={() => setImportConfirmOpen(false)}
                onConfirm={confirmBulkCreate}
            />

            <V2Modal
                open={resultOpen}
                onClose={() => setResultOpen(false)}
                title="Embarques criados"
                description="Cada linha é um processo por fornecedor, com todos os pedidos e clientes vinculados."
                size="2xl"
                footer={
                    <Button variant="outline" onClick={() => setResultOpen(false)}>
                        Fechar
                    </Button>
                }
            >
                <ul className="space-y-3">
                    {lastCreated.map((s) => (
                        <li key={s.id} className="rounded-lg border border-gray-200 p-3 text-sm">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <Link to={`/shipments/${s.id}`} className="font-semibold text-primary hover:underline">
                                    {s.order_number ?? `#${s.id}`}
                                </Link>
                                <span className="text-xs text-gray-500">{s.supplier ?? '—'}</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-600">
                                {s.deals_count} pedido(s):{' '}
                                {(s.linked_deals ?? []).map((d) => d.label).join(', ') || '—'}
                            </p>
                            <div className="mt-2 flex items-center gap-2">
                                <span className="text-[10px] font-semibold uppercase text-gray-400">Clientes</span>
                                <ClientAvatars names={s.clients_summary ?? []} maxVisible={5} size="sm" />
                            </div>
                        </li>
                    ))}
                </ul>
            </V2Modal>

            <PloomesDealListFilters
                filters={filters}
                options={filterOptions ?? {}}
                optionsLoading={filterOptions === undefined}
                showPanel={showFilterPanel}
                onChange={patchFilters}
                onTogglePanel={() => setShowFilterPanel((v) => !v)}
                onClear={clearFilters}
            />

            <PloomesDealBulkBar
                count={selectedIds.size}
                loading={bulkMutation.isPending}
                onCreate={handleBulkCreate}
                onClear={() => setSelectedIds(new Set())}
            />

            {selectedIds.size > 0 ? (
                <PloomesDealImportPreviewPanel selectedIds={selectedIdsArray} />
            ) : null}

            {isFetching && !isLoading && <p className="text-xs text-gray-400">Atualizando…</p>}

            <PloomesDealListTable
                rows={rows}
                loading={isLoading}
                page={filters.page}
                perPage={filters.per_page}
                lastPage={meta?.last_page ?? 1}
                total={meta?.total ?? 0}
                sort={filters.sort || '-finish_date'}
                onSortChange={(sort) => patchFilters({ sort, page: 1 })}
                onPageChange={(page) => patchFilters({ page })}
                onPerPageChange={(per_page) => patchFilters({ per_page, page: 1 })}
                selectedIds={selectedIds}
                onToggleRow={toggleRow}
                onToggleAll={toggleAll}
            />
        </div>
    );
}
